import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { ZInput, ZSelect } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import { CURRENCIES } from "@/lib/format";
import { Trash2, Plus, Save } from "lucide-react";

export default function AdminSettingsPage() {
  const [s, setS] = useState({ commission_pct: 8, free_event_fee: 0, free_event_fee_currency: "GNF", free_event_fee_tiers: [], payout_methods: [] });
  const [loading, setLoading] = useState(true);
  const [newMethod, setNewMethod] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setErr(""); setSuccess("");
    setSaving(true);
    try {
      const payload = {
        commission_pct: parseFloat(s.commission_pct) || 0,
        free_event_fee: parseFloat(s.free_event_fee) || 0,
        free_event_fee_currency: s.free_event_fee_currency,
        free_event_fee_tiers: (s.free_event_fee_tiers || []).map((t) => ({
          max_attendees: parseInt(t.max_attendees) || 0,
          fee: parseFloat(t.fee) || 0,
        })),
        payout_methods: s.payout_methods,
      };
      const r = await api.put("/admin/settings", payload);
      setS(r.data);
      setSuccess("Paramètres enregistrés.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setErr(errMsg(e));
    } finally { setSaving(false); }
  };

  const addTier = () => setS((p) => ({ ...p, free_event_fee_tiers: [...(p.free_event_fee_tiers || []), { max_attendees: 100, fee: 0 }] }));
  const updateTier = (i, k, v) => setS((p) => ({ ...p, free_event_fee_tiers: p.free_event_fee_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const removeTier = (i) => setS((p) => ({ ...p, free_event_fee_tiers: p.free_event_fee_tiers.filter((_, idx) => idx !== i) }));

  const addMethod = () => {
    if (!newMethod.trim()) return;
    if (s.payout_methods.includes(newMethod.trim())) return;
    setS({ ...s, payout_methods: [...s.payout_methods, newMethod.trim()] });
    setNewMethod("");
  };

  const removeMethod = (m) => {
    setS({ ...s, payout_methods: s.payout_methods.filter((x) => x !== m) });
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;

  return (
    <div className="z-container" style={{ maxWidth: 640 }} data-testid="admin-settings-page">
      <h1 className="z-page-title">Paramètres plateforme</h1>
      <p className="z-page-sub">Configuration de la commission et des frais.</p>

      <div className="z-card" style={{ padding: 24 }}>
        <h3 style={{ marginTop: 0, fontSize: 15, fontWeight: 700, color: "var(--z-muted)" }}>Commission événements payants</h3>
        <ZInput
          type="number" min="0" max="100" step="0.5"
          label="Commission (%)" value={s.commission_pct}
          onChange={(e) => setS({ ...s, commission_pct: e.target.value })}
          help="Pourcentage prélevé par la plateforme sur les recettes brutes de chaque événement payant."
          data-testid="setting-commission"
        />

        <div className="z-divider" />

        <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--z-muted)" }}>Forfait événements gratuits / privés — par paliers</h3>
        <p className="z-help" style={{ marginTop: -8, marginBottom: 10 }}>
          Montant facturé selon le nombre de personnes attendues. Le palier « Au-delà » (laisser <strong>0</strong>) s&apos;applique aux capacités supérieures à tous les autres paliers.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
          {(s.free_event_fee_tiers || []).map((t, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr auto", gap: 8, alignItems: "end" }} data-testid={`fee-tier-${i}`}>
              <ZInput
                type="number" min="0" label="Jusqu'à (pers.)"
                value={t.max_attendees}
                onChange={(e) => updateTier(i, "max_attendees", e.target.value)}
                help={Number(t.max_attendees) === 0 ? "Au-delà" : undefined}
                data-testid={`fee-tier-max-${i}`}
              />
              <ZInput
                type="number" min="0" step="100" label="Forfait"
                value={t.fee}
                onChange={(e) => updateTier(i, "fee", e.target.value)}
                data-testid={`fee-tier-fee-${i}`}
              />
              <button onClick={() => removeTier(i)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)", marginBottom: 14 }} data-testid={`fee-tier-remove-${i}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
          <button onClick={addTier} className="z-btn z-btn-outline z-btn-sm" data-testid="fee-tier-add"><Plus size={14} /> Ajouter un palier</button>
          <ZSelect
            label="" value={s.free_event_fee_currency}
            onChange={(e) => setS({ ...s, free_event_fee_currency: e.target.value })}
            data-testid="setting-free-fee-currency"
          >
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </ZSelect>
        </div>

        <div className="z-divider" />

        <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--z-muted)" }}>Méthodes de paiement disponibles</h3>
        <p className="z-help" style={{ marginTop: -8, marginBottom: 10 }}>
          Liste proposée lors de l'enregistrement d'un paiement ou d'une facturation.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
          {s.payout_methods.map((m) => (
            <div key={m} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--z-bg)", borderRadius: 10 }} data-testid={`method-${m}`}>
              <span style={{ flex: 1, fontSize: 14 }}>{m}</span>
              <button onClick={() => removeMethod(m)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)" }} data-testid={`method-remove-${m}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="z-input" value={newMethod}
            onChange={(e) => setNewMethod(e.target.value)}
            placeholder="Ex: PayPal, Stripe, etc."
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMethod())}
            data-testid="method-input"
          />
          <button onClick={addMethod} className="z-btn z-btn-outline" data-testid="method-add"><Plus size={16} /></button>
        </div>

        {err && <p className="z-error-text" style={{ marginTop: 14 }}>{err}</p>}
        {success && <p style={{ marginTop: 14, color: "var(--z-success)", fontWeight: 600 }}>{success}</p>}

        <ZButton onClick={save} block loading={saving} style={{ marginTop: 18 }} data-testid="setting-save-btn">
          <Save size={16} /> Enregistrer les paramètres
        </ZButton>
      </div>
    </div>
  );
}
